import { useCreditHistory } from '../../hooks/useCreditHistory';
import { History, ChevronDown } from 'lucide-react';

const typeBadgeStyles = {
  purchase: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  processing: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  refund: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
};

function formatDate(timestamp) {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function TypeBadge({ type }) {
  const style = typeBadgeStyles[type] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style}`}>
      {type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Unknown'}
    </span>
  );
}

function AmountDisplay({ type, amount }) {
  const isPositive = type === 'purchase' || type === 'refund';
  return (
    <span className={`font-semibold ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
      {isPositive ? '+' : '-'}{Math.abs(amount || 0)}
    </span>
  );
}

export default function CreditHistorySection() {
  const { transactions, loading, hasMore, loadMore } = useCreditHistory();

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-dark-border">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <History className="w-5 h-5" />
            Credit History
          </h2>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto" />
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading transactions...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="p-12 text-center">
            <History className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No transactions yet.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-dark-bg-secondary">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Credits</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                        {formatDate(tx.timestamp)}
                      </td>
                      <td className="px-6 py-4">
                        <TypeBadge type={tx.type} />
                      </td>
                      <td className="px-6 py-4 text-right text-sm">
                        <AmountDisplay type={tx.type} amount={tx.amount} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                        {tx.reference || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100 dark:divide-dark-border">
              {transactions.map((tx) => (
                <div key={tx.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <TypeBadge type={tx.type} />
                    <AmountDisplay type={tx.type} amount={tx.amount} />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(tx.timestamp)}
                  </div>
                  {tx.reference && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 truncate">
                      {tx.reference}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Load More */}
        {hasMore && (
          <div className="p-4 border-t border-gray-200 dark:border-dark-border text-center">
            <button
              onClick={loadMore}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition"
            >
              <ChevronDown className="w-4 h-4" />
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
